import { createRouterSelector, getRouterSelectors } from '@ngrx/router-store';
import {
  ActivatedRouteSnapshot,
  Data as RouterData,
  Params,
} from '@angular/router';
import {
  createSelector,
  DefaultProjectorFn,
  MemoizedSelector,
} from '@ngrx/store';
import { ChildRouterLink, SubNavigationRouterData } from '@/models';
import { RouteData } from '@/models/route-data';

const {
  selectCurrentRoute: internalSelectCurrentRoute,
  selectRouteData: internalSelectRouteData,
  selectUrl: internalSelectUrl,
} = getRouterSelectors();

export const selectCurrentRoute = internalSelectCurrentRoute;
export const selectUrl = internalSelectUrl;

export const selectRouteData = internalSelectRouteData as MemoizedSelector<
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Record<string, any>,
  RouteData,
  DefaultProjectorFn<RouteData>
>;

export const selectClientSpecificRouteSettings = createSelector(
  selectRouteData,
  (data) => data?.clientSpecificSettings,
);

export const selectRouteNestedParams = createSelector(
  createRouterSelector(),
  (router) => {
    let currentRoute = router?.state?.root;
    let params: Params = {};
    while (currentRoute?.firstChild) {
      currentRoute = currentRoute.firstChild;
      params = {
        ...params,
        ...currentRoute.params,
      };
    }
    return params;
  },
);

export const selectRouteNestedParam = (param: string) =>
  createSelector(selectRouteNestedParams, (params) => params && params[param]);

export const selectChildRouterLinks = createSelector(
  selectCurrentRoute,
  selectUrl,
  selectRouteData,
  (currentRoute, url, data): ChildRouterLink[] => {
    const parentRoute = url.replace(
      (currentRoute as ActivatedRouteSnapshot).url.toString(),
      '',
    );
    return (
      (data as RouterData & SubNavigationRouterData)?.childRouterLinks || []
    ).map((link) => ({
      route: `${parentRoute}/${link.route}`,
      label: link.label,
      originalRoute: link.route,
    }));
  },
);

export const selectSelectedChildRouterLink = createSelector(
  selectCurrentRoute,
  selectChildRouterLinks,
  (
    currentRoute: ActivatedRouteSnapshot,
    childRouterLinks,
  ): ChildRouterLink | null => {
    return (
      (
        (childRouterLinks as (ChildRouterLink & { originalRoute: string })[]) ??
        []
      ).find((l) =>
        currentRoute.url.some((seg) => seg.path === l.originalRoute),
      ) || null
    );
  },
);
